/**
 * Admin — Database Pegawai (biodata lengkap)
 *
 * Menampilkan daftar seluruh pegawai + status kelengkapan biodata mandiri.
 * Klik pegawai untuk melihat profil lengkap (read-only).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Search, RefreshCw, FileText, ImageIcon, Filter, UserCircle2, ChevronRight,
  Wallet, Pencil, Save, X, Upload, Trash2, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { AuthImage } from "@/components/AuthImage";
import { api, apiErrorMessage } from "@/lib/api";

const ANY = "__ANY__";

// Struktur field yang ditampilkan pada modal detail (harus sinkron dengan backend).
const DETAIL_SECTIONS = [
  {
    title: "Identitas & Domisili",
    fields: [
      ["nomor_induk_kependudukan_(nik)", "NIK"],
      ["tempat_lahir_pegawai", "Tempat Lahir"],
      ["tanggal_lahir_pegawai", "Tanggal Lahir"],
      ["jenis_kelamin_pegawai", "Jenis Kelamin"],
      ["agama", "Agama"],
      ["status_kewarganegaraan", "Kewarganegaraan"],
      ["nomor_handphone", "No. Handphone"],
      ["nomor_kartu_keluarga", "No. Kartu Keluarga"],
      ["alamat_sesuai_ktp", "Alamat KTP"],
      ["alamat_domisili", "Alamat Domisili"],
    ],
  },
  {
    title: "Pendidikan, Pajak & Rekening",
    fields: [
      ["tingkat_pendidikan_terakhir", "Pendidikan Terakhir"],
      ["jurusan_pendidikan", "Jurusan"],
      ["status_pajak_ptkp", "Status Pajak (PTKP)"],
      ["npwp", "NPWP"],
      ["nama_bank", "Nama Bank"],
      ["no_rekening", "No. Rekening"],
    ],
  },
  {
    title: "Data Pasangan",
    fields: [
      ["status_pernikahan", "Status Pernikahan"],
      ["nama_istri/suami", "Nama Pasangan"],
      ["jenis_kelamin_istri/suami", "Jenis Kelamin Pasangan"],
      ["tempat_lahir_istri/suami", "Tempat Lahir Pasangan"],
      ["tanggal_lahir_istri/suami", "Tanggal Lahir Pasangan"],
    ],
  },
  {
    title: "Fasilitas Kesehatan",
    fields: [
      ["nama_fasilitas_kesehatan_(sesuai_bpjs_kesehatan)", "Nama Faskes BPJS"],
      ["lokasi_fasilitas_kesehatan_(sesuai_bpjs_kesehatan)", "Lokasi Faskes BPJS"],
      ["nama_fasilitas_kesehatan_(yang_sering_dikunjungi)", "Nama Faskes Rutin"],
      ["lokasi_fasilitas_kesehatan_(yang_sering_dikunjungi)", "Lokasi Faskes Rutin"],
    ],
  },
];

const UPLOAD_FIELDS = [
  ["field_upload_foto_profil", "Foto Profil", true],
  ["field_upload_nik", "Scan KTP", false],
  ["field_upload_kartu_keluarga", "Scan Kartu Keluarga", false],
  ["field_upload_ijazah", "Scan Ijazah", false],
];

function fmtDateISO(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });
}

// Semua field text/select/date yang bisa diedit oleh admin (upload dokumen
// tetap self-service oleh pegawai). Struktur menentukan tipe & options
// untuk rendering form.
const OPTIONS = {
  jk: ["LAKI - LAKI", "PEREMPUAN"],
  agama: ["ISLAM", "KRISTEN", "KATOLIK", "HINDU", "BUDDHA", "KONGHUCU", "LAINNYA"],
  kwn: ["WNI", "WNA"],
  pdd: ["SD", "SMP", "SMA / SMK", "DIPLOMA 1", "DIPLOMA 2", "DIPLOMA 3", "STRATA 1", "STRATA 2", "STRATA 3"],
  ptkp: ["TK/0", "TK/1", "TK/2", "TK/3", "K/0", "K/1", "K/2", "K/3"],
  pernikahan: ["BELUM MENIKAH", "SUDAH MENIKAH", "CERAI HIDUP", "CERAI MATI"],
};

const EDIT_FIELDS = [
  { name: "nomor_induk_kependudukan_(nik)", label: "NIK", type: "text", maxLength: 16 },
  { name: "tempat_lahir_pegawai", label: "Tempat Lahir", type: "text" },
  { name: "tanggal_lahir_pegawai", label: "Tanggal Lahir", type: "date" },
  { name: "jenis_kelamin_pegawai", label: "Jenis Kelamin", type: "select", options: OPTIONS.jk },
  { name: "agama", label: "Agama", type: "select", options: OPTIONS.agama },
  { name: "status_kewarganegaraan", label: "Kewarganegaraan", type: "select", options: OPTIONS.kwn },
  { name: "nomor_handphone", label: "No. Handphone", type: "text" },
  { name: "nomor_kartu_keluarga", label: "No. Kartu Keluarga", type: "text", maxLength: 16 },
  { name: "alamat_sesuai_ktp", label: "Alamat KTP", type: "textarea" },
  { name: "alamat_domisili", label: "Alamat Domisili", type: "textarea" },
  { name: "tingkat_pendidikan_terakhir", label: "Pendidikan Terakhir", type: "select", options: OPTIONS.pdd },
  { name: "jurusan_pendidikan", label: "Jurusan", type: "text" },
  { name: "status_pajak_ptkp", label: "Status Pajak (PTKP)", type: "select", options: OPTIONS.ptkp },
  { name: "npwp", label: "NPWP", type: "text", maxLength: 20 },
  { name: "nama_bank", label: "Nama Bank", type: "text" },
  { name: "no_rekening", label: "No. Rekening", type: "text" },
  { name: "status_pernikahan", label: "Status Pernikahan", type: "select", options: OPTIONS.pernikahan },
  { name: "nama_istri/suami", label: "Nama Pasangan", type: "text" },
  { name: "jenis_kelamin_istri/suami", label: "JK Pasangan", type: "select", options: OPTIONS.jk },
  { name: "tempat_lahir_istri/suami", label: "Tempat Lahir Pasangan", type: "text" },
  { name: "tanggal_lahir_istri/suami", label: "Tanggal Lahir Pasangan", type: "date" },
  { name: "nama_fasilitas_kesehatan_(sesuai_bpjs_kesehatan)", label: "Nama Faskes BPJS", type: "text" },
  { name: "lokasi_fasilitas_kesehatan_(sesuai_bpjs_kesehatan)", label: "Lokasi Faskes BPJS", type: "text" },
  { name: "nama_fasilitas_kesehatan_(yang_sering_dikunjungi)", label: "Nama Faskes Rutin", type: "text" },
  { name: "lokasi_fasilitas_kesehatan_(yang_sering_dikunjungi)", label: "Lokasi Faskes Rutin", type: "text" },
];

// Field anak dinamis (25 field: 5 anak × 5 field)
for (let i = 1; i <= 5; i++) {
  EDIT_FIELDS.push(
    { name: `nama_anak_${i}`, label: `Nama Anak ${i}`, type: "text" },
    { name: `jenis_kelamin_anak_${i}`, label: `JK Anak ${i}`, type: "select", options: OPTIONS.jk },
    { name: `tempat_lahir_anak_${i}`, label: `Tempat Lahir Anak ${i}`, type: "text" },
    { name: `tanggal_lahir_anak_${i}`, label: `Tanggal Lahir Anak ${i}`, type: "date" },
  );
}

function EditFieldRow({ def, value, onChange }) {
  if (def.type === "select") {
    return (
      <div className="space-y-1">
        <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{def.label}</Label>
        <Select value={value || ""} onValueChange={(v) => onChange(def.name, v)}>
          <SelectTrigger data-testid={`edit-${def.name}`}><SelectValue placeholder="Pilih…" /></SelectTrigger>
          <SelectContent>
            {def.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (def.type === "textarea") {
    return (
      <div className="space-y-1 sm:col-span-2">
        <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{def.label}</Label>
        <Textarea rows={2} value={value || ""} onChange={(e) => onChange(def.name, e.target.value)} data-testid={`edit-${def.name}`} />
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{def.label}</Label>
      <Input type={def.type || "text"} value={value || ""} maxLength={def.maxLength} onChange={(e) => onChange(def.name, e.target.value)} data-testid={`edit-${def.name}`} />
    </div>
  );
}

/**
 * Kartu upload untuk mode admin — mendukung Unggah / Ganti / Hapus dokumen
 * biodata milik pegawai target. Simpan di object storage dengan path NPP target.
 */
function AdminUploadCard({ npp, fieldKey, label, isImg, biodata, onChanged, onZoom }) {
  const [busy, setBusy] = useState(false);
  const path = biodata?.[fieldKey];
  const filename = biodata?.[`${fieldKey}_filename`];
  const uploader = biodata?.[`${fieldKey}_uploaded_by_admin_nama`];
  const accept = isImg
    ? "image/jpeg,image/png,image/webp"
    : "application/pdf,image/jpeg,image/png,image/webp";

  async function handleUpload(file) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran file maksimal 5 MB");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/biodata/${npp}/upload/${fieldKey}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${label} untuk ${npp} berhasil diunggah`);
      onChanged(data.biodata);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleClear() {
    if (!window.confirm(`Hapus dokumen ${label} milik ${npp}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/biodata/${npp}/upload/${fieldKey}`);
      toast.success("Dokumen dihapus");
      onChanged(data.biodata);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex flex-col gap-2" data-testid={`admin-upload-card-${fieldKey}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">{label}</div>
        {path ? (
          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-1.5 py-0.5">
            <CheckCircle2 size={10} /> Ada
          </span>
        ) : null}
      </div>
      {path ? (
        isImg ? (
          <AuthImage
            path={path} kind="biodata" alt={label}
            className="w-full h-24 rounded cursor-pointer"
            onClick={() => onZoom({ path, label })}
          />
        ) : (
          <button
            onClick={() => onZoom({ path, label })}
            className="w-full h-24 rounded flex flex-col items-center justify-center bg-white border border-slate-200 text-teal-700 hover:bg-teal-50 transition"
          >
            <FileText size={20} />
            <span className="text-[10px] font-semibold mt-1 truncate max-w-full px-1">
              {filename || "Buka"}
            </span>
          </button>
        )
      ) : (
        <div className="w-full h-24 rounded flex items-center justify-center bg-white border border-dashed border-slate-200 text-slate-400 text-xs">
          Belum diunggah
        </div>
      )}
      {uploader ? (
        <div className="text-[9px] text-slate-500 italic truncate" title={`Diunggah oleh: ${uploader}`}>
          oleh Admin: {uploader}
        </div>
      ) : null}
      <div className="flex gap-1.5">
        <label className="inline-flex flex-1">
          <input
            type="file"
            accept={accept}
            className="hidden"
            data-testid={`admin-upload-input-${fieldKey}`}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              handleUpload(f);
            }}
          />
          <Button
            asChild
            size="sm"
            className="w-full h-7 text-xs gap-1 bg-teal-700 hover:bg-teal-800 cursor-pointer"
            disabled={busy}
            data-testid={`admin-upload-btn-${fieldKey}`}
          >
            <span><Upload size={11} /> {path ? "Ganti" : "Unggah"}</span>
          </Button>
        </label>
        {/* Tombol Hapus file dihilangkan (Keamanan Data — Feb 2026). Admin
           cukup menekan "Ganti" untuk menggantikan file yang sudah diunggah. */}
      </div>
    </div>
  );
}


function anakList(bio) {
  const out = [];
  for (let i = 1; i <= 5; i++) {
    const nama = bio[`nama_anak_${i}`];
    if (!nama) continue;
    out.push({
      idx: i,
      nama,
      jk: bio[`jenis_kelamin_anak_${i}`] || "—",
      tempat: bio[`tempat_lahir_anak_${i}`] || "—",
      tanggal: bio[`tanggal_lahir_anak_${i}`] || "—",
    });
  }
  return out;
}

export default function AdminBiodata() {
  const [rows, setRows] = useState([]);
  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState(ANY);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [zoomPath, setZoomPath] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  async function handleSyncPayroll() {
    if (!window.confirm(
      "Sinkronkan Biodata → Profil Payroll untuk SEMUA pegawai?\n\n" +
      "NPWP, Nama Bank, No. Rekening, dan Status PTKP di Profil Payroll akan " +
      "ditimpa dengan nilai dari Biodata (bila terisi). Field kosong di Biodata dilewati."
    )) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/biodata/sync-payroll");
      toast.success(
        `Sinkronisasi selesai — ${data.updated} pegawai diperbarui` +
        (data.profil_baru ? ` (${data.profil_baru} profil baru dibuat)` : "") +
        (data.skipped_kosong ? ` · ${data.skipped_kosong} dilewati (biodata kosong)` : "")
      );
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSyncing(false);
    }
  }

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (unitId !== ANY) params.unit_id = unitId;
      if (q.trim()) params.q = q.trim();
      const [b, u] = await Promise.all([
        api.get("/biodata", { params }),
        units.length ? Promise.resolve({ data: units }) : api.get("/master/unit"),
      ]);
      setRows(b.data.rows || []);
      if (!units.length) setUnits(u.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [unitId]);

  async function openDetail(row) {
    setDetailLoading(true);
    setEditMode(false);
    setDetail({ user: { npp: row.npp, name: row.nama_pegawai, unit_nama: row.unit_nama }, biodata: null });
    try {
      const { data } = await api.get(`/biodata/${row.npp}`);
      setDetail(data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }

  function enterEditMode() {
    if (!detail?.biodata) return;
    // Copy hanya field yang bisa diedit
    const draft = {};
    EDIT_FIELDS.forEach((f) => { draft[f.name] = detail.biodata[f.name] || ""; });
    setEditForm(draft);
    setEditMode(true);
  }

  async function saveEdit() {
    if (!detail?.user?.npp) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/biodata/${detail.user.npp}`, editForm);
      toast.success("Biodata pegawai diperbarui");
      // Refresh detail dengan data terbaru
      setDetail((prev) => ({ ...prev, biodata: data }));
      setEditMode(false);
      // Refresh list agar persen kelengkapan ter-update
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  const stats = useMemo(() => {
    const total = rows.length;
    const lengkap = rows.filter((r) => r.kelengkapan_persen >= 90).length;
    const kosong = rows.filter((r) => !r.has_biodata).length;
    const sedang = total - lengkap - kosong;
    return { total, lengkap, sedang, kosong };
  }, [rows]);

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="page-admin-biodata">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">SDM</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Database Pegawai
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Profil lengkap seluruh pegawai — hasil pengisian mandiri Modul Biodata.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="gap-2" data-testid="biodata-admin-refresh">
          <RefreshCw size={14} /> Muat Ulang
        </Button>
      </div>

      {/* Aksi Batch */}
      <div className="mt-4 rounded-xl border border-teal-100 bg-teal-50/40 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-xs text-slate-700 leading-relaxed">
          <div className="font-semibold text-slate-900 flex items-center gap-1.5">
            <Wallet size={14} className="text-teal-700" /> Sinkron ke Profil Payroll
          </div>
          Setiap kali pegawai menyimpan Biodata, NPWP / Nama Bank / No. Rekening / PTKP
          otomatis mengalir ke Profil Payroll. Tombol ini melakukan backfill sekaligus
          untuk seluruh pegawai (biodata kosong dilewati).
        </div>
        <Button
          onClick={handleSyncPayroll}
          disabled={syncing}
          className="gap-2 bg-teal-700 hover:bg-teal-800 shrink-0"
          data-testid="biodata-sync-payroll-btn"
        >
          <Wallet size={14} /> {syncing ? "Menyinkronkan…" : "Sinkronkan Semua Pegawai"}
        </Button>
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="Total Pegawai" value={stats.total} tone="slate" testId="stat-total" />
        <StatCard label="Terisi ≥ 90%" value={stats.lengkap} tone="emerald" testId="stat-lengkap" />
        <StatCard label="Sedang Diisi" value={stats.sedang} tone="amber" testId="stat-sedang" />
        <StatCard label="Belum Diisi" value={stats.kosong} tone="rose" testId="stat-kosong" />
      </div>

      {/* Filters */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-slate-500 mb-4">
          <Filter size={12} /> Filter
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Unit Kerja</Label>
            <Select value={unitId} onValueChange={setUnitId}>
              <SelectTrigger className="mt-1.5" data-testid="filter-unit">
                <SelectValue placeholder="Semua Unit" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Semua Unit</SelectItem>
                {units.map((u) => (<SelectItem key={u.id} value={u.id}>{u.nama}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Cari (Nama / NPP)</Label>
            <div className="mt-1.5 relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Ketik nama atau NPP…"
                className="pl-9"
                data-testid="filter-q"
              />
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={load} className="bg-teal-700 hover:bg-teal-800" data-testid="filter-apply">
            Terapkan Filter
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Pegawai</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Unit / Jabatan</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Kelengkapan</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Dokumen</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Terakhir Diubah</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">Memuat…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-14">
                    <UserCircle2 size={30} className="mx-auto text-slate-300" />
                    <div className="mt-3 font-semibold text-slate-700">Tidak ada pegawai pada filter ini</div>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.user_id} data-testid={`row-${r.npp}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <TableCell>
                      <div className="font-semibold text-slate-900">{r.nama_pegawai}</div>
                      <div className="text-xs text-slate-500 font-mono-clock">{r.npp} · {r.email}</div>
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">
                      <div className="font-semibold text-slate-800">{r.unit_nama}</div>
                      <div className="text-xs text-slate-500">{r.jabatan_nama}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-[140px]">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${r.kelengkapan_persen >= 90 ? "bg-emerald-600" : r.kelengkapan_persen >= 40 ? "bg-amber-500" : "bg-rose-500"}`}
                            style={{ width: `${r.kelengkapan_persen}%` }}
                          />
                        </div>
                        <div className="text-xs font-bold text-slate-800 font-mono-clock w-9 text-right">{r.kelengkapan_persen}%</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-slate-700 font-mono-clock text-sm">{r.jumlah_upload}/4</TableCell>
                    <TableCell className="text-slate-600 text-xs">{fmtDateISO(r.updated_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1 text-teal-800"
                        onClick={() => openDetail(r)}
                        data-testid={`detail-btn-${r.npp}`}
                      >
                        Lihat Detail <ChevronRight size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) { setDetail(null); setEditMode(false); } }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="biodata-detail-dialog">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <DialogTitle>{detail?.user?.name || "Profil Pegawai"}</DialogTitle>
                <DialogDescription>
                  {detail?.user ? `${detail.user.npp} · ${detail.user.unit_nama || "-"} · ${detail.user.jabatan_nama || "-"}` : ""}
                </DialogDescription>
              </div>
              {detail?.biodata && !editMode ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={enterEditMode}
                  className="gap-1.5 shrink-0 text-teal-800 border-teal-200 hover:bg-teal-50"
                  data-testid="biodata-edit-btn"
                >
                  <Pencil size={13} /> Edit Biodata
                </Button>
              ) : null}
              {editMode ? (
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditMode(false)}
                    disabled={saving}
                    className="gap-1.5"
                    data-testid="biodata-edit-cancel"
                  >
                    <X size={13} /> Batal
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveEdit}
                    disabled={saving}
                    className="gap-1.5 bg-teal-700 hover:bg-teal-800"
                    data-testid="biodata-edit-save"
                  >
                    <Save size={13} /> {saving ? "Menyimpan…" : "Simpan"}
                  </Button>
                </div>
              ) : null}
            </div>
          </DialogHeader>
          {detailLoading || !detail?.biodata ? (
            <div className="py-10 text-center text-slate-500 text-sm">Memuat detail…</div>
          ) : editMode ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-amber-100 bg-amber-50/70 p-3 text-xs text-amber-900">
                <strong>Mode Edit Admin</strong> — perubahan otomatis tersimpan sebagai
                data biodata pegawai dan menyinkronkan NPWP / Nama Bank / No. Rekening /
                PTKP ke Profil Payroll. File upload tetap dikelola oleh pegawai.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {EDIT_FIELDS.map((f) => (
                  <EditFieldRow
                    key={f.name}
                    def={f}
                    value={editForm[f.name]}
                    onChange={(k, v) => setEditForm((prev) => ({ ...prev, [k]: v }))}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Foto profil menonjol di atas */}
              <div className="flex items-start gap-5">
                <AuthImage
                  path={detail.biodata.field_upload_foto_profil}
                  kind="biodata"
                  alt="Foto Profil"
                  className="w-24 h-24 rounded-full border border-slate-200"
                  testId="detail-foto-profil"
                />
                <div>
                  <div className="text-lg font-bold text-slate-900">{detail.user.name}</div>
                  <div className="text-xs text-slate-500 font-mono-clock">{detail.user.npp} · {detail.user.email}</div>
                  <div className="text-xs text-slate-600 mt-1">
                    {detail.user.unit_nama} · {detail.user.jabatan_nama}
                  </div>
                </div>
              </div>

              {DETAIL_SECTIONS.map((sec) => (
                <div key={sec.title} className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-teal-700 mb-3">{sec.title}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {sec.fields.map(([k, label]) => (
                      <div key={k} className="text-sm">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
                        <div className="text-slate-900 mt-0.5 break-words" data-testid={`detail-${k}`}>
                          {detail.biodata[k] || <span className="text-slate-400">—</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Anak (dinamis) */}
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-teal-700 mb-3">Data Anak</div>
                {anakList(detail.biodata).length === 0 ? (
                  <div className="text-sm text-slate-500">Tidak ada data anak.</div>
                ) : (
                  <ul className="space-y-2">
                    {anakList(detail.biodata).map((a) => (
                      <li key={a.idx} className="text-sm border-b border-slate-100 pb-2 last:border-none last:pb-0">
                        <div className="font-semibold text-slate-900">Anak ke-{a.idx}: {a.nama}</div>
                        <div className="text-xs text-slate-600">
                          {a.jk} · {a.tempat}{a.tanggal !== "—" ? ` · ${a.tanggal}` : ""}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Dokumen upload — Admin dapat mengunggah/mengganti/menghapus */}
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-teal-700 mb-1">
                  Dokumen Biodata
                </div>
                <p className="text-[11px] text-slate-500 mb-3">
                  Admin dapat mengunggah dokumen atas nama pegawai — file tersimpan
                  di folder pegawai (<code className="font-mono-clock">/biodata/{detail.user.npp}/</code>).
                  Maks 5 MB · PDF / JPG / PNG / WEBP.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {UPLOAD_FIELDS.map(([k, label, isImg]) => (
                    <AdminUploadCard
                      key={k}
                      npp={detail.user.npp}
                      fieldKey={k}
                      label={label}
                      isImg={isImg}
                      biodata={detail.biodata}
                      onChanged={(newBio) => {
                        setDetail((prev) => ({ ...prev, biodata: newBio }));
                        // Refresh list agar count upload akurat
                        load();
                      }}
                      onZoom={setZoomPath}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Zoom / preview dokumen */}
      <Dialog open={!!zoomPath} onOpenChange={(v) => !v && setZoomPath(null)}>
        <DialogContent className="max-w-3xl" data-testid="biodata-zoom-dialog">
          <DialogHeader><DialogTitle>{zoomPath?.label || "Dokumen"}</DialogTitle></DialogHeader>
          {zoomPath ? (
            zoomPath.path.endsWith(".pdf") ? (
              <div className="py-8 text-center">
                <FileText size={48} className="mx-auto text-teal-700" />
                <div className="mt-3 text-sm text-slate-700">Dokumen PDF</div>
                <a
                  href="#"
                  onClick={async (e) => {
                    e.preventDefault();
                    const resp = await api.get(`/biodata/file/${zoomPath.path}`, { responseType: "blob" });
                    const url = URL.createObjectURL(resp.data);
                    window.open(url, "_blank", "noopener");
                  }}
                  className="mt-3 inline-block text-teal-800 font-semibold underline text-sm"
                >
                  Buka di Tab Baru
                </a>
              </div>
            ) : (
              <AuthImage
                path={zoomPath.path}
                kind="biodata"
                alt={zoomPath.label}
                className="w-full max-h-[70vh] rounded-lg"
              />
            )
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, tone, testId }) {
  const tones = {
    slate: "border-slate-200 text-slate-900",
    emerald: "border-emerald-200 text-emerald-800",
    amber: "border-amber-200 text-amber-800",
    rose: "border-rose-200 text-rose-800",
  };
  return (
    <div className={`rounded-xl border bg-white p-5 ${tones[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{label}</div>
      <div className="mt-1 font-mono-clock font-bold text-3xl leading-tight">{value}</div>
    </div>
  );
}
